import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Share2 } from "lucide-react";
import logo from "../../Images/Logo.svg";
import certificateIcon from "../../Images/certificate.svg";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config/api";

//Helper Functions
const normalizeUserData = (userData) => {
  return userData?.user || userData || {};
};

const extractUserName = (normalizedUser) => {
  return (
    normalizedUser.full_name ||
    normalizedUser.name ||
    (normalizedUser.first_name && normalizedUser.last_name
      ? `${normalizedUser.first_name} ${normalizedUser.last_name}`
      : null) ||
    normalizedUser.username ||
    normalizedUser.fullName ||
    "User"
  );
};

const getUserInitials = (name) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};

const getUserId = (normalizedUser) => {
  return normalizedUser.user_id || normalizedUser.id || "N/A";
};

const getAvatarUrl = (normalizedUser) => {
  return normalizedUser.avatar_url || null;
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
  });
};

// Star Rating Component
const StarRating = ({ rating = 4.8 }) => {
  return (
    <div className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill={star <= Math.floor(rating) ? "#FFA500" : "none"}
          stroke="#FFA500"
          strokeWidth="1"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
};

// Course Card Component
const CourseCard = ({ course = {}, userName = "User" }) => {
  const navigate = useNavigate();

  const handleShareCertificate = async () => {
    try {
      const shareUrl = window.location.href;

      if (navigator.share) {
        await navigator.share({
          title: `${course.title || "Course"} Certificate`,
          text: `Check out my certificate for ${
            course.title || "this course"
          } from SkillsMD!`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert("Certificate link copied to clipboard!");
      }
    } catch (error) {
      console.error("Error sharing certificate:", error);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Top Section - Certificate Icon + Course Details */}
      <div className="flex gap-6">
        {/* Certificate Icon */}
        <div className="w-20 h-20 bg-slate-50 rounded border border-slate-300 flex items-center justify-center flex-shrink-0">
          <img src={certificateIcon} alt="Certificate" />
        </div>

        {/* Course Details */}
        <div className="flex-1 space-y-3">
          {/* Title + Date + Duration + Buttons */}
          <div className="flex items-start justify-between">
            {/* LEFT STACK */}
            <div className="flex flex-col">
              <h3 className="flex items-start text-cyan-600 text-lg font-medium hover:underline cursor-pointer">
                {course.title || "Course Name"}
              </h3>

              <p className="flex items-start text-neutral-600 text-sm mt-1">
                {formatDate(course.course_end_date)}
              </p>

              <p className="flex items-start text-neutral-600 text-sm">
                {course.duration || "N/A"}
              </p>
            </div>

            {/* RIGHT BUTTONS */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleShareCertificate}
                className="px-4 py-2 bg-[#4AACC5] rounded text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#3a99b0] transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share Certificate
              </button>

              <button
                onClick={() =>
                  navigate(
                    `/accomplishment/view_certificate?certificate_id=${course.certificate_id}`,
                  )
                }
                className="px-4 py-2 rounded border border-[#4AACC5] text-[#4AACC5] text-sm font-semibold hover:bg-teal-50 transition-colors"
              >
                View Certificate
              </button>
            </div>
          </div>

          {/* Grade */}
          <p className="text-sm">
            <span className="flex items-start text-neutral-600">
              Grade Achieved :
              <span className=" text-stone-900 font-semibold">
                {course.grade ? `${course.grade}%` : "N/A"}
              </span>{" "}
            </span>
          </p>

          {/* Verification Text */}
          <p className="flex items-start text-neutral-700 text-sm leading-relaxed">
            {userName}'s account is verified. SkillsMD certifies their
            successful completion of {course.title || "this course"}.
          </p>

          {/* Divider */}
          <div className="border-t border-gray-200 my-4" />
        </div>
      </div>

      {/* Bottom Section - Logo + Course Info */}
      <div className="flex gap-6 mt-4">
        {/* Logo Icon - Aligned with certificate icon above */}
        <div className="w-20 h-10  rounded-sm  flex items-center justify-center flex-shrink-0">
          <img
            src={logo}
            alt="SkillsMD"
            className="h-6 max-w-full object-contain"
          />
        </div>

        {/* Course Info + Rating */}
        <div className="flex-1 space-y-3">
          {/* Course Title and SkillsMD */}
          <div>
            <h4 className="flex items-start text-cyan-600 text-base font-medium">
              {course.title || "Course Name"}
            </h4>
            <p className="flex items-start text-neutral-600 text-xs">
              SkillsMD
            </p>
          </div>

          {/* Rating */}
          <div className="flex items-start gap-2">
            <StarRating rating={course.rating || 0} />
            <span className="text-neutral-600 text-sm">
              {course.rating || 0} ({course.ratingsCount?.toLocaleString() || 0}{" "}
              ratings) | {course.enrolled?.toLocaleString() || 0} already
              enrolled
            </span>
          </div>

          {/* Review Link */}
          <button
            onClick={() =>
              navigate("/accomplishment/write_review", {
                state: {
                  courseId: course.course_id,
                  enrollmentId: course.enrollment_id,
                  programName: course.title,
                },
              })
            }
            className="flex items-start text-cyan-600 text-sm font-medium hover:underline cursor-pointer"
          >
            Give Your Ratings for this Course
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AccomplishmentsCard() {
  const { userData } = useOutletContext() || {};
  const [imageError, setImageError] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  //Use Helper Functions
  const normalizedUser = normalizeUserData(userData);
  const nameToUse = extractUserName(normalizedUser);
  const avatarUrl = getAvatarUrl(normalizedUser);
  const hasAvatar = avatarUrl && !imageError;
  const userInitials = getUserInitials(nameToUse);
  const userId = getUserId(normalizedUser);

  const handleImageError = () => setImageError(true);

  // Fetch certificates from API
  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("access_token");
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        const response = await axios.get(
          `${API_URL}/certificates/my-certificates`,
          { headers },
        );

        setCertificates(response.data || []);
      } catch (err) {
        console.error("Error fetching certificates:", err);

        // If backend says no data found → treat as empty state
        if (
          err.response?.status === 404 ||
          err.response?.data?.detail?.toLowerCase().includes("no enrollment")
        ) {
          setCertificates([]);
          setError(null);
        } else {
          setError(
            err.response?.data?.detail ||
              "Failed to load certificates. Please try again later.",
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCertificates();
  }, []);

  return (
    <div className="font-['Roboto']">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <h1 className="flex items-start text-zinc-800 text-xl font-bold leading-6">
          Accomplishments
        </h1>

        {/* Profile Verification Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          {/* Profile Section */}
          <div className="flex items-center gap-6 mb-8">
            {/* Profile Image */}
            <div className="w-20 h-20 rounded-lg overflow-hidden border-2 bg-gray-100">
              {hasAvatar ? (
                <img
                  src={avatarUrl}
                  alt="avatar"
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                />
              ) : (
                <div className="w-full h-full bg-[#4AACC5] flex items-center justify-center text-white font-bold text-sm">
                  {userInitials}
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex flex-col gap-3">
              {/* ID Badge */}
              <div className="inline-flex">
                <span className="px-3 py-1 bg-[#4AACC5] rounded-lg text-white text-sm font-normal leading-5">
                  ID #{userId}
                </span>
              </div>

              {/* Name */}
              <h2 className="text-zinc-800 text-xl font-bold">{nameToUse}</h2>
            </div>
          </div>

          {/* Verification Message */}
          <div className="flex items-start text-neutral-700 text-xl font-normal leading-8">
            Your name, {nameToUse}, is verified. This is the name that will
            appear on your certificates.
            {/* If you have questions or need help with
            your ID Verification, visit our{" "}
            <a
              href="#"
              className="text-cyan-600 font-normal underline leading-8 hover:text-cyan-700 transition-colors"
            >
              ID Verification support page
            </a> */}
            {/* . */}
          </div>
        </div>

        {/* My Courses Section */}
        <div className="space-y-4">
          <h2 className="flex items-start text-zinc-800 text-xl font-bold leading-6">
            My Programs
          </h2>

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your certificates...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-white rounded-lg border border-red-200 p-6 text-center">
              <p className="text-red-600">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && certificates.length === 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <p className="text-gray-600 text-lg mb-2">No certificates yet</p>
              <p className="text-gray-500 text-sm">
                Complete a course to earn your first certificate!
              </p>
            </div>
          )}

          {/* Course Cards */}
          {!loading &&
            !error &&
            certificates.length > 0 &&
            certificates.map((course) => (
              <CourseCard
                key={course.certificate_id}
                course={course}
                userName={nameToUse}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
